/**
 * <s-market-map> — One interactive map (Leaflet + OpenStreetMap) with a pin for every location.
 *
 * On load the map zooms out to fit every pin. Buttons anywhere on the page drive it:
 *   command="--show-market" — Zooms to the invoking button's pin and opens its popup.
 *   command="--show-all"    — Zooms back out to show every pin in the current day filter.
 * Day filter: radios named "find-us-day" (value "all" or a day). Picking a day keeps only pins whose
 * button sits inside [data-day="<day>"], plus grocery stores (no day), and refits the map to them.
 *
 * Required child markup:
 *   [data-map-canvas]          — Element the map renders into.
 *   [data-map-caption] (opt.)  — Text showing what the map is showing.
 *   [data-map-show-all] (opt.) — "Show all" button, shown only while zoomed to one pin.
 *   [data-map-fallback] (opt.) — Loading message, removed once the map renders.
 *
 * Host attributes: data-all-label, data-directions-label.
 * Host CSS custom properties: --pin-market (market pins), --pin-store (grocery store pins).
 *
 * Location buttons ([commandfor=<id>][command="--show-market"]):
 *   data-lat, data-lng     — Pin position (required).
 *   data-map-label         — Location name.
 *   data-map-when          — Schedule or address line for the popup.
 *   data-map-directions    — Directions URL for the popup link.
 *   data-map-kind="store"  — Grocery store: round pin in --pin-store, drawn on top, shown for every day.
 */
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const LEAFLET_CSS_SRI = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
const LEAFLET_JS_SRI = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
const FIT = { padding: [28, 28], maxZoom: 13 };

let leafletPromise;
function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = LEAFLET_CSS;
    link.integrity = LEAFLET_CSS_SRI;
    link.crossOrigin = '';
    document.head.append(link);

    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.integrity = LEAFLET_JS_SRI;
    script.crossOrigin = '';
    script.onload = () => (window.L ? resolve(window.L) : reject(new Error('Leaflet missing')));
    script.onerror = () => reject(new Error('Leaflet failed to load'));
    document.head.append(script);
  });
  return leafletPromise;
}

class SMarketMap extends HTMLElement {
  #ac;
  #map;
  #L;
  #markers = new Map();
  #stores = [];
  #day = 'all';
  #pending;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.addEventListener('command', (e) => this.#onCommand(e), { signal });
    document.addEventListener(
      'change',
      (e) => {
        const input = e.target;
        if (input instanceof HTMLInputElement && input.name === 'find-us-day' && input.checked) {
          this.#filter(input.value, input.closest('label')?.textContent.trim());
        }
      },
      { signal },
    );
    this.#init();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#map?.remove();
    this.#map = undefined;
    this.#markers.clear();
  }

  get #buttons() {
    return [...document.querySelectorAll(`[commandfor="${this.id}"][command="--show-market"]`)];
  }

  get #reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #cssVar(name, fallback) {
    return getComputedStyle(this).getPropertyValue(name).trim() || fallback;
  }

  async #init() {
    const canvas = this.querySelector('[data-map-canvas]');
    const fallback = this.querySelector('[data-map-fallback]');
    if (!canvas) return;

    let L;
    try {
      L = await loadLeaflet();
    } catch {
      if (fallback) fallback.textContent = '';
      return;
    }
    if (!this.isConnected || this.#map) return;
    this.#L = L;

    fallback?.remove();
    const map = L.map(canvas, { scrollWheelZoom: false, zoomSnap: 0.25 });
    this.#map = map;
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    const marketColor = this.#cssVar('--pin-market', this.#cssVar('--color-accent', '#b5532b'));
    const storeColor = this.#cssVar('--pin-store', '#4F6B3A');
    const directionsLabel = this.dataset.directionsLabel || 'Get directions';

    for (const button of this.#buttons) {
      const lat = parseFloat(button.dataset.lat);
      const lng = parseFloat(button.dataset.lng);
      if (Number.isNaN(lat) || Number.isNaN(lng)) continue;

      const isStore = button.dataset.mapKind === 'store';
      const marker = isStore
        ? L.circleMarker([lat, lng], { radius: 8, color: '#ffffff', weight: 2, fillColor: storeColor, fillOpacity: 1 })
        : L.circleMarker([lat, lng], { radius: 8, color: '#ffffff', weight: 2, fillColor: marketColor, fillOpacity: 1 });
      marker.addTo(map);

      const popup = document.createElement('div');
      const name = document.createElement('strong');
      name.textContent = button.dataset.mapLabel || '';
      popup.append(name);
      if (button.dataset.mapWhen) {
        const when = document.createElement('div');
        when.textContent = button.dataset.mapWhen;
        popup.append(when);
      }
      if (button.dataset.mapDirections) {
        const link = document.createElement('a');
        link.href = button.dataset.mapDirections;
        link.target = '_blank';
        link.rel = 'noopener';
        link.textContent = directionsLabel;
        popup.append(link);
      }
      marker.bindPopup(popup);
      if (button.dataset.mapLabel) marker.bindTooltip(button.dataset.mapLabel);
      marker.on('click', () => this.#select(button, false));
      this.#markers.set(button, marker);
      if (isStore) this.#stores.push(marker);
    }

    const checked = document.querySelector('input[name="find-us-day"]:checked');
    this.#day = checked?.value || 'all';
    this.#applyFilter();
    this.#fit(false);

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => map.invalidateSize());
      ro.observe(canvas);
      this.#ac.signal.addEventListener('abort', () => ro.disconnect());
    }

    if (this.#pending) {
      const pending = this.#pending;
      this.#pending = undefined;
      pending();
    }
  }

  #isVisible(button) {
    if (this.#day === 'all') return true;
    const group = button.closest('[data-day]');
    return !group || group.dataset.day === this.#day;
  }

  #applyFilter() {
    queueMicrotask(() => this.#stores.forEach((m) => this.#map?.hasLayer(m) && m.bringToFront()));
    if (!this.#map) return;
    for (const [button, marker] of this.#markers) {
      const show = this.#isVisible(button);
      if (show && !this.#map.hasLayer(marker)) marker.addTo(this.#map);
      if (!show && this.#map.hasLayer(marker)) this.#map.removeLayer(marker);
    }
  }

  #fit(animate) {
    if (!this.#map || !this.#L) return;
    const points = [...this.#markers].filter(([b]) => this.#isVisible(b)).map(([, m]) => m.getLatLng());
    if (!points.length) {
      this.#map.setView([47.6, -122.3], 9);
      return;
    }
    const bounds = this.#L.latLngBounds(points);
    if (animate && !this.#reduceMotion) this.#map.flyToBounds(bounds, { ...FIT, duration: 0.8 });
    else this.#map.fitBounds(bounds, FIT);
  }

  #filter(day, label) {
    this.#day = day || 'all';
    this.#allCaption = this.#day === 'all' ? this.dataset.allLabel : label || this.dataset.allLabel;
    if (!this.#map) return;
    this.#map.closePopup();
    this.#applyFilter();
    this.#fit(true);
    this.#setCaption(this.#allCaption, false);
    for (const other of this.#buttons) other.setAttribute('aria-pressed', 'false');
  }

  #allCaption;

  #onCommand(e) {
    if (e.command === '--show-market' && e.source) {
      const run = () => this.#select(e.source, true);
      this.#map ? run() : (this.#pending = run);
      if (window.matchMedia('(max-width: 1023px)').matches) {
        this.scrollIntoView({ behavior: this.#reduceMotion ? 'auto' : 'smooth', block: 'center' });
      }
    } else if (e.command === '--show-all') {
      this.#showAll();
    }
  }

  #select(button, move) {
    const marker = this.#markers.get(button);
    if (!marker || !this.#map) return;
    if (!this.#map.hasLayer(marker)) marker.addTo(this.#map);
    if (move) {
      const target = marker.getLatLng();
      if (this.#reduceMotion) this.#map.setView(target, 14);
      else this.#map.flyTo(target, 14, { duration: 0.8 });
    }
    marker.openPopup();
    this.#setCaption(button.dataset.mapLabel, true);
    for (const other of this.#buttons) other.setAttribute('aria-pressed', other === button ? 'true' : 'false');
  }

  #showAll() {
    if (!this.#map) return;
    this.#map.closePopup();
    this.#fit(true);
    this.#setCaption(this.#allCaption || this.dataset.allLabel, false);
    for (const other of this.#buttons) other.setAttribute('aria-pressed', 'false');
  }

  #setCaption(text, focused) {
    const caption = this.querySelector('[data-map-caption]');
    if (caption && text) caption.textContent = text;
    const showAll = this.querySelector('[data-map-show-all]');
    if (showAll) showAll.hidden = !focused;
  }
}

if (!customElements.get('s-market-map')) customElements.define('s-market-map', SMarketMap);
